import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import SavedViews from '../components/SavedViews';
import EmptyState from '../components/common/EmptyState';
import DisabledFeature from '../components/common/DisabledFeature';
import {
  Users, CheckCircle, XCircle, AlertCircle, UserCheck, Ban,
  Search, RefreshCw, BarChart3, Filter, Gauge, TrendingUp, Clock
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#64748b', '#db2777'];

const CARD_META = [
  { key: 'applications_received', label: 'Applications Received', icon: Users, color: 'text-blue-700', testid: 'ops-card-received' },
  { key: 'eligible', label: 'Eligible', icon: CheckCircle, color: 'text-green-700', testid: 'ops-card-eligible' },
  { key: 'ineligible', label: 'Ineligible', icon: XCircle, color: 'text-amber-700', testid: 'ops-card-ineligible' },
  { key: 'auto_rejected', label: 'Auto Rejected', icon: Ban, color: 'text-red-700', testid: 'ops-card-auto-rejected' },
  { key: 'manual_review', label: 'Manual Review', icon: AlertCircle, color: 'text-amber-700', testid: 'ops-card-manual-review' },
  { key: 'ready_for_recruiter', label: 'Ready For Recruiter', icon: UserCheck, color: 'text-blue-700', testid: 'ops-card-ready' },
];

export default function OperationsDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [distributions, setDistributions] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [apps, setApps] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [workflowF, setWorkflowF] = useState('');
  const [poolF, setPoolF] = useState('');
  const [screeningF, setScreeningF] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [tab, setTab] = useState('overview');

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.append('search', search);
    if (statusF) p.append('status', statusF);
    if (workflowF) p.append('workflow', workflowF);
    if (poolF) p.append('talent_pool', poolF);
    if (screeningF) p.append('screening', screeningF);
    if (dateFrom) p.append('date_from', dateFrom);
    return p;
  }, [search, statusF, workflowF, poolF, screeningF, dateFrom]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const base = buildParams();
      const appParams = new URLSearchParams(base);
      appParams.append('page', String(page));
      appParams.append('page_size', '25');

      const [ov, di, fu, tl, ap] = await Promise.all([
        fetch(`${API_URL}/api/operations/overview?${base}`, { headers }),
        fetch(`${API_URL}/api/operations/distributions?${base}`, { headers }),
        fetch(`${API_URL}/api/operations/funnel?${base}`, { headers }),
        fetch(`${API_URL}/api/operations/timeline?${base}&granularity=day`, { headers }),
        fetch(`${API_URL}/api/operations/applications?${appParams}`, { headers }),
      ]);

      if (!ov.ok || !di.ok || !fu.ok || !tl.ok || !ap.ok) {
        if ([ov, di, fu, tl, ap].some(r => r.status === 403)) {
          throw new Error('This feature is currently disabled.');
        }
        throw new Error('Failed to load operations data.');
      }
      setOverview(await ov.json());
      setDistributions(await di.json());
      setFunnel((await fu.json()).funnel || []);
      setTimeline((await tl.json()).timeline || []);
      setApps(await ap.json());
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [token, buildParams, page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setPage(1); }, [search, statusF, workflowF, poolF, screeningF, dateFrom]);

  const chart = (key) => (distributions?.[key] || []).map(d => ({ name: d.key, count: d.count }));
  const isDisabled = (error || '').toLowerCase().includes('disabled');

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="operations-dashboard">
        <Breadcrumb items={[{ label: 'Applicant Intelligence' }]} />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-700" /> Applicant Intelligence
            </h1>
            <p className="text-sm text-gray-600">Read-only recruiting analytics over the applicant pipeline (eligibility, screening, funnel &amp; talent pool). This is not freight/load operations data.</p>
          </div>
          <button
            onClick={fetchAll}
            data-testid="ops-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {isDisabled ? (
          <DisabledFeature name="Applicant Intelligence" note="Applicant analytics are currently unavailable because the underlying source feature is disabled. Configuration is preserved; no action is required." />
        ) : (
        <>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
            <div className="flex-1 relative min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text" placeholder="Search by name or email..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="ops-search-input"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="ops-filter-status"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Statuses</option>
              {['new', 'reviewed', 'contacted', 'in_progress', 'approved', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={workflowF} onChange={(e) => setWorkflowF(e.target.value)} data-testid="ops-filter-workflow"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Workflows</option>
              {['NOT_ROUTED', 'READY_FOR_RECRUITER', 'MANUAL_REVIEW', 'AUTO_REJECTED', 'SCREENING_PENDING'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={poolF} onChange={(e) => setPoolF(e.target.value)} data-testid="ops-filter-pool"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Pools</option>
              {['NONE', 'ACTIVE_POOL', 'REVIEW_POOL', 'FUTURE_POOL', 'REJECTED_POOL', 'NOT_ELIGIBLE'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={screeningF} onChange={(e) => setScreeningF(e.target.value)} data-testid="ops-filter-screening"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Screening</option>
              {['NOT_EVALUATED', 'NOT_CONFIGURED', 'PASSED', 'NEEDS_REVIEW', 'FAILED'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ops-filter-date"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white" />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6" data-testid="ops-error">
            <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {error}</div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-gray-200" data-testid="ops-tabs">
          <button onClick={() => setTab('overview')} data-testid="ops-tab-overview"
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === 'overview' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Overview
          </button>
          <button onClick={() => setTab('kpis')} data-testid="ops-tab-kpis"
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 inline-flex items-center gap-1 ${tab === 'kpis' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Gauge className="w-4 h-4" /> KPIs
          </button>
          <button onClick={() => setTab('sla')} data-testid="ops-tab-sla"
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 inline-flex items-center gap-1 ${tab === 'sla' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Clock className="w-4 h-4" /> SLA
          </button>
        </div>

        {tab === 'kpis' && <KpiPanel token={token} paramStr={buildParams().toString()} />}
        {tab === 'sla' && <SlaPanel token={token} paramStr={buildParams().toString()} />}

        {tab === 'overview' && (loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="ops-loading">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading operations data...
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              {CARD_META.map(({ key, label, icon: Icon, color, testid }) => (
                <div key={key} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className="text-2xl font-bold text-gray-900">{overview?.[key] ?? 0}</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{label}</p>
                </div>
              ))}
            </div>

            {/* Funnel + Timeline */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="ops-funnel">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Filter className="w-4 h-4" /> Pipeline Funnel</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={funnel} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="stage" width={130} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="ops-timeline">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Submission Timeline (daily)</h3>
                {timeline.length === 0 ? (
                  <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm" data-testid="ops-timeline-empty">No submissions in range</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Distribution charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <DistCard title="Workflow Distribution" testid="ops-chart-workflow" data={chart('workflow_status')} kind="bar" />
              <DistCard title="Talent Pool Distribution" testid="ops-chart-pool" data={chart('talent_pool')} kind="pie" />
              <DistCard title="Screening Distribution" testid="ops-chart-screening" data={chart('screening_status')} kind="bar" />
              <DistCard title="Eligibility Distribution" testid="ops-chart-eligibility" data={chart('eligibility_status')} kind="pie" />
            </div>

            {/* Recent applications */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="ops-table-recent">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Recent Applications ({apps.total})</h3>
              </div>
              {apps.items.length === 0 ? (
                <div className="py-12 text-center text-gray-400" data-testid="ops-table-empty">No applications match your filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        {['Name', 'Email', 'Status', 'Eligibility', 'Screening', 'Workflow', 'Pool', 'Submitted'].map(h => (
                          <th key={h} className="px-4 py-2 text-left font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {apps.items.map(a => (
                        <tr key={a.id} data-testid={`ops-row-${a.id}`}>
                          <td className="px-4 py-2 text-gray-900">{a.first_name} {a.last_name}</td>
                          <td className="px-4 py-2 text-gray-600">{a.email}</td>
                          <td className="px-4 py-2 text-gray-600">{a.status}</td>
                          <td className="px-4 py-2 text-gray-600">{a.eligibility_status}</td>
                          <td className="px-4 py-2 text-gray-600">{a.screening_status}</td>
                          <td className="px-4 py-2 text-gray-600">{a.workflow_status}</td>
                          <td className="px-4 py-2 text-gray-600">{a.talent_pool}</td>
                          <td className="px-4 py-2 text-gray-500">{(a.created_at || '').slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {apps.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="ops-prev-page"
                    className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 disabled:opacity-50">Previous</button>
                  <span className="text-sm text-gray-600">Page {apps.page} of {apps.total_pages}</span>
                  <button disabled={page >= apps.total_pages} onClick={() => setPage(p => p + 1)} data-testid="ops-next-page"
                    className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 disabled:opacity-50">Next</button>
                </div>
              )}
            </div>
          </>
        ))}
        </>
        )}
      </div>
    </DashboardLayout>
  );
}

function DistCard({ title, testid, data, kind }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {(!data || data.length === 0) ? (
        <div className="h-[240px] flex items-center justify-center"><EmptyState variant="no-records" compact title="No records yet" message="No applicants in this distribution for the current filters." /></div>
      ) : kind === 'pie' ? (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const PCT = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);
const SIGNED_PCT = (v) => (v === 'insufficient_data' || v === null || v === undefined
  ? 'Insufficient data' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);
const DURATION = (s) => {
  if (s === 'insufficient_data' || s === null || s === undefined) return 'Insufficient data';
  const h = Math.floor(s / 3600); const m = Math.round((s % 3600) / 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const PRIMARY_META = [
  { key: 'eligibility_rate', label: 'Eligibility Rate' },
  { key: 'auto_reject_rate', label: 'Auto-Reject Rate' },
  { key: 'screening_pass_rate', label: 'Screening Pass Rate' },
  { key: 'ready_for_recruiter_rate', label: 'Ready-for-Recruiter Rate' },
  { key: 'manual_review_rate', label: 'Manual Review Rate' },
  { key: 'rejection_rate', label: 'Rejection Rate' },
  { key: 'talent_pool_assignment_rate', label: 'Talent Pool Assignment Rate' },
  { key: 'rejection_email_delivery_rate', label: 'Rejection Email Delivery Rate' },
];
const CONV_META = [
  { key: 'received_to_eligible', label: 'Received → Eligible' },
  { key: 'eligible_to_screened', label: 'Eligible → Screened' },
  { key: 'screened_to_workflow_routed', label: 'Screened → Workflow Routed' },
];
const CYCLE_META = [
  { key: 'avg_time_to_claim_seconds', label: 'Avg Time to Claim' },
  { key: 'avg_time_to_assign_seconds', label: 'Avg Time to Assign' },
  { key: 'avg_time_since_last_activity_seconds', label: 'Avg Time Since Last Activity' },
];

function KpiPanel({ token, paramStr }) {
  const [loading, setLoading] = React.useState(true);
  const [disabled, setDisabled] = React.useState(false);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`${API_URL}/api/operational-kpis?${paramStr}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        if (r.status === 403) { setDisabled(true); return; }
        if (!r.ok) throw new Error('Failed to load operational KPIs.');
        setData(await r.json()); setDisabled(false);
      } catch (e) { if (active) setError(e.message); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [token, paramStr]);

  if (disabled) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4" data-testid="kpi-disabled">
        Operational KPIs are currently disabled.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid="kpi-loading">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading KPIs...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4" data-testid="kpi-error">
        <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {error}</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="kpi-panel" className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Gauge className="w-4 h-4 text-blue-700" /> Primary KPIs
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PRIMARY_META.map(({ key, label }) => (
            <div key={key} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`kpi-${key}`}>
              <span className="text-2xl font-bold text-gray-900">{PCT(data.primary[key])}</span>
              <p className="text-xs text-gray-600 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="kpi-conversions">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Funnel Conversions</h3>
          <ul className="divide-y divide-gray-100">
            {CONV_META.map(({ key, label }) => (
              <li key={key} className="flex items-center justify-between py-2" data-testid={`kpi-${key}`}>
                <span className="text-sm text-gray-700">{label}</span>
                <span className="text-sm font-semibold text-gray-900">{PCT(data.funnel_conversions[key])}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="kpi-trends">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-700" /> Trends
          </h3>
          <ul className="divide-y divide-gray-100">
            <li className="flex items-center justify-between py-2" data-testid="kpi-avg_submissions_per_day">
              <span className="text-sm text-gray-700">Avg Submissions / Day</span>
              <span className="text-sm font-semibold text-gray-900">
                {data.trends.avg_submissions_per_day ?? '—'}
              </span>
            </li>
            <li className="flex items-center justify-between py-2" data-testid="kpi-volume_trend_pct">
              <span className="text-sm text-gray-700">Volume Trend (vs prior period)</span>
              <span className="text-sm font-semibold text-gray-900">{SIGNED_PCT(data.trends.volume_trend_pct)}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="kpi-cycle-times">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-700" /> Cycle-Time KPIs
          <span className="text-xs font-normal text-gray-400">(informational)</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          {CYCLE_META.map(({ key, label }) => (
            <div key={key} className="border border-gray-100 rounded-lg p-3" data-testid={`kpi-${key}`}>
              <span className="text-lg font-semibold text-gray-900">{DURATION(data.cycle_times[key])}</span>
              <p className="text-xs text-gray-600 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-xs text-gray-400 space-y-1" data-testid="kpi-disclosures">
        <p>{data.meta?.disclosures?.rejection_email_delivery_rate}</p>
        <p>{data.meta?.disclosures?.cycle_times}</p>
      </div>
    </div>
  );
}


function SlaPanel({ token, paramStr }) {
  const [loading, setLoading] = React.useState(true);
  const [disabled, setDisabled] = React.useState(false);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`${API_URL}/api/sla-monitoring?${paramStr}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        if (r.status === 403) { setDisabled(true); return; }
        if (!r.ok) throw new Error('Failed to load SLA monitoring.');
        setData(await r.json()); setDisabled(false);
      } catch (e) { if (active) setError(e.message); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [token, paramStr]);

  if (disabled) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4" data-testid="sla-disabled">
        SLA monitoring is currently disabled.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid="sla-loading">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading SLA monitoring...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4" data-testid="sla-error">
        <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {error}</div>
      </div>
    );
  }
  if (!data) return null;

  const rows = data.items || data.results || data.rows || [];
  return (
    <div data-testid="sla-panel" className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-700" /> SLA Monitoring
        {data.total != null && <span className="text-xs font-normal text-gray-400">({data.total})</span>}
      </h3>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-500 border border-gray-100 rounded-lg p-4" data-testid="sla-empty">No SLA records for the current filters.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2">Applicant</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Stage / SLA</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((it, i) => (
                <tr key={it.id || i} data-testid={`sla-row-${i}`}>
                  <td className="px-3 py-2 text-gray-800">{it.full_name || it.name || it.applicant_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{it.status || it.eligibility || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{it.sla_status || it.workflow || it.stage || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
